import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
    BellRing, Plus, Loader2, MapPin, Trash2, Sparkles, Mail, ArrowRight,
} from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import api, { formatApiError } from "@/lib/api";
import { toast } from "@/components/ui/sonner";

const REMOTE_OPTIONS = [
    { value: "all", label: "Any workplace" },
    { value: "remote", label: "Remote" },
    { value: "hybrid", label: "Hybrid" },
    { value: "on_site", label: "On-site" },
];
const TYPES = ["All", "Full-time", "Part-time", "Contract", "Temporary", "Internship"];
const FREQUENCIES = [
    { value: "instant", label: "Instant — the moment a matching role goes live" },
    { value: "daily", label: "Daily — 08:00 UTC digest" },
    { value: "weekly", label: "Weekly — every Tuesday" },
];

const emptyForm = () => ({
    keyword: "", location: "", remote_status: "all", employment_type: "All", frequency: "weekly",
});

const JobAlerts = () => {
    const [alerts, setAlerts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [form, setForm] = useState(emptyForm());
    const [saving, setSaving] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const { data } = await api.get("/talent/job-alerts");
            setAlerts(data.results || []);
        } catch (e) { toast.error(formatApiError(e)); }
        finally { setLoading(false); }
    }, []);
    useEffect(() => { load(); }, [load]);

    const create = async (e) => {
        e.preventDefault();
        if (!form.keyword.trim() && !form.location.trim() && form.remote_status === "all" && form.employment_type === "All") {
            toast.error("Set at least one filter — a keyword, a location, a remote type, or an employment type.");
            return;
        }
        setSaving(true);
        try {
            const { data } = await api.post("/talent/job-alerts", form);
            if (data.duplicate) {
                toast.info("You already have this alert.");
            } else {
                const cadence = FREQUENCIES.find((f) => f.value === form.frequency)?.label || form.frequency;
                toast.success(`Alert saved — cadence: ${cadence.split(" — ")[0]}.`);
            }
            setForm(emptyForm());
            await load();
        } catch (err) { toast.error(formatApiError(err)); }
        finally { setSaving(false); }
    };

    const remove = async (id) => {
        try {
            await api.delete(`/talent/job-alerts/${id}`);
            setAlerts((r) => r.filter((a) => a.id !== id));
            toast.success("Alert removed.");
        } catch (err) { toast.error(formatApiError(err)); }
    };

    return (
        <div data-testid="talent-job-alerts-page" className="space-y-8">
            <div className="flex items-start justify-between flex-wrap gap-3">
                <div>
                    <p className="overline">Job discovery</p>
                    <h1 className="mt-1 font-heading font-medium text-2xl sm:text-3xl tracking-tight text-foreground">Job Alerts</h1>
                    <p className="text-sm text-muted-foreground mt-1 max-w-xl">
                        Save the filters that matter. Pick your cadence — instant, daily, or weekly — and Simonara will only email when a matching role goes live.
                    </p>
                </div>
                <Link to="/careers" className="btn-outline text-sm">
                    Browse all roles <ArrowRight size={13} className="ml-1.5" />
                </Link>
            </div>

            {/* Create form */}
            <form onSubmit={create} className="card-soft p-5 space-y-4" data-testid="alert-form">
                <div className="flex items-center gap-2 text-emerald-700 dark:text-emerald-400">
                    <Plus size={14} /> <span className="text-sm font-semibold">Create a new alert</span>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    <div>
                        <Label>Keyword</Label>
                        <Input data-testid="alert-keyword" value={form.keyword} onChange={(e) => setForm({ ...form, keyword: e.target.value })} placeholder="e.g. HR Business Partner" className="mt-1.5" />
                    </div>
                    <div>
                        <Label>Location</Label>
                        <div className="relative mt-1.5">
                            <MapPin size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                            <Input data-testid="alert-location" value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} placeholder="City, ST or Remote" className="pl-9" />
                        </div>
                    </div>
                    <div>
                        <Label>Workplace</Label>
                        <Select value={form.remote_status} onValueChange={(v) => setForm({ ...form, remote_status: v })}>
                            <SelectTrigger data-testid="alert-remote" className="mt-1.5 bg-background">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                {REMOTE_OPTIONS.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </div>
                    <div>
                        <Label>Employment type</Label>
                        <Select value={form.employment_type} onValueChange={(v) => setForm({ ...form, employment_type: v })}>
                            <SelectTrigger data-testid="alert-type" className="mt-1.5 bg-background">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                {TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </div>
                    <div className="md:col-span-2">
                        <Label>Delivery frequency</Label>
                        <Select value={form.frequency} onValueChange={(v) => setForm({ ...form, frequency: v })}>
                            <SelectTrigger data-testid="alert-frequency" className="mt-1.5 bg-background">
                                <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                                {FREQUENCIES.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                            </SelectContent>
                        </Select>
                    </div>
                </div>
                <button type="submit" disabled={saving} data-testid="alert-create" className="btn-green disabled:opacity-60">
                    {saving ? <><Loader2 size={13} className="mr-1.5 inline animate-spin" /> Saving…</> : <><BellRing size={13} className="mr-1.5 inline" /> Save alert</>}
                </button>
            </form>

            {/* Existing alerts */}
            <section>
                <div className="flex items-center justify-between mb-3">
                    <h2 className="font-heading font-semibold text-lg text-foreground">Active alerts</h2>
                    <span className="text-xs text-muted-foreground">{alerts.length} saved</span>
                </div>
                {loading ? (
                    <div className="py-10 flex justify-center"><Loader2 size={18} className="animate-spin text-emerald-500" /></div>
                ) : alerts.length === 0 ? (
                    <div data-testid="alert-empty" className="card-soft p-10 text-center">
                        <BellRing size={22} className="mx-auto text-emerald-500" />
                        <h3 className="mt-3 font-heading font-semibold text-base text-foreground">No alerts yet.</h3>
                        <p className="mt-1.5 text-sm text-muted-foreground max-w-md mx-auto">
                            Save your favorite filters above and we&apos;ll email you a curated shortlist every Tuesday.
                        </p>
                    </div>
                ) : (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {alerts.map((a) => (
                            <motion.div key={a.id} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} data-testid={`alert-row-${a.id}`} className="card-soft p-4">
                                <div className="flex items-start justify-between gap-3">
                                    <div className="min-w-0">
                                        <div className="flex flex-wrap gap-1.5">
                                            {a.keyword && <Badge className="bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border-0 text-[10px]">{a.keyword}</Badge>}
                                            {a.location && <Badge variant="outline" className="text-[10px]"><MapPin size={9} className="mr-0.5" />{a.location}</Badge>}
                                            {a.remote_status && a.remote_status !== "all" && (
                                                <Badge variant="outline" className="text-[10px] capitalize">{a.remote_status}</Badge>
                                            )}
                                            {a.employment_type && a.employment_type !== "All" && (
                                                <Badge variant="outline" className="text-[10px]">{a.employment_type}</Badge>
                                            )}
                                            <Badge data-testid={`alert-frequency-badge-${a.id}`} className="bg-sky-500/15 text-sky-700 dark:text-sky-400 border-0 text-[10px] capitalize">
                                                {a.frequency || "weekly"}
                                            </Badge>
                                        </div>
                                        <div className="mt-2 text-xs text-muted-foreground inline-flex items-center gap-1.5">
                                            <Sparkles size={11} className="text-emerald-500" />
                                            <strong className="text-foreground">{a.match_count ?? 0}</strong> matching role{a.match_count === 1 ? "" : "s"} today
                                        </div>
                                        <div className="mt-1 text-[11px] text-muted-foreground inline-flex items-center gap-1.5">
                                            <Mail size={10} />
                                            {a.last_sent_at
                                                ? `Last sent ${new Date(a.last_sent_at).toLocaleDateString()}`
                                                : a.frequency === "instant"
                                                    ? "Fires the moment a matching role publishes"
                                                    : a.frequency === "daily"
                                                        ? "Daily digest starts tomorrow at 08:00 UTC"
                                                        : "First digest ships next Tuesday"}
                                        </div>
                                    </div>
                                    <button
                                        onClick={() => remove(a.id)}
                                        data-testid={`alert-delete-${a.id}`}
                                        className="p-1.5 rounded-full text-muted-foreground hover:text-rose-600 hover:bg-rose-500/10"
                                        aria-label="Remove alert"
                                    >
                                        <Trash2 size={13} />
                                    </button>
                                </div>
                            </motion.div>
                        ))}
                    </div>
                )}
            </section>
        </div>
    );
};

export default JobAlerts;
